/**
 * Anonymous attempt bearer token storage.
 *
 * Anonymous (free-trial) attempts have no user account to key ownership on, so
 * the backend returns a high-entropy `anonymousToken` once when the attempt is
 * started and requires it on every later attempt-scoped request.
 *
 * Stored in sessionStorage, keyed by attemptId:
 * - survives a page refresh (so an in-progress anonymous attempt is resumable),
 * - is scoped to one browser tab, so it is not shared with other tabs or other
 *   attempts,
 * - is not persisted beyond the tab, unlike localStorage.
 *
 * Authenticated attempts never receive a token and simply store nothing.
 */
const STORAGE_PREFIX = 'attemptToken:';

export function setAttemptToken(attemptId: string, token: string): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + attemptId, token);
  } catch {
    // Storage can be unavailable (private mode/quota); the request will simply
    // be refused by the backend if the token cannot be carried.
  }
}

export function getAttemptToken(attemptId: string): string | null {
  try {
    return sessionStorage.getItem(STORAGE_PREFIX + attemptId);
  } catch {
    return null;
  }
}

export function clearAttemptToken(attemptId: string): void {
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + attemptId);
  } catch {
    // Ignore.
  }
}
